import { useTranslation } from "react-i18next";
import { FunnelSimpleIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";

export type TLedgerFilterOption<V extends string> = { value: V; label: string; count: number };

function FilterSection<V extends string>({
  label,
  options,
  selected,
  onToggle,
  testId
}: {
  label: string;
  options: TLedgerFilterOption<V>[];
  selected: Set<V>;
  onToggle: (value: V, checked: boolean) => void;
  testId: string;
}) {
  return (
    <div className="border-b border-innoveraZinc-100 py-1 last:border-0">
      <div className="px-2 py-2 text-[14px] font-medium leading-[13px] text-innoveraZinc-900">{label}</div>
      {options.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-2 text-[14px] text-innoveraZinc-700 hover:bg-innoveraZinc-50"
        >
          <Checkbox
            checked={selected.has(option.value)}
            onCheckedChange={(checked) => onToggle(option.value, checked)}
            className="[&_svg]:h-3 [&_svg]:w-3"
            data-testid={`${testId}-${option.value}`}
          />
          <span className="min-w-0 flex-1 truncate">{option.label}</span>
          <span className="text-[12px] text-innoveraZinc-500">{option.count}</span>
        </label>
      ))}
    </div>
  );
}

export function LedgerFilterPopover<R extends string, S extends string>({
  roles,
  sources,
  selectedRoles,
  selectedSources,
  onToggleRole,
  onToggleSource,
  onReset
}: {
  roles: TLedgerFilterOption<R>[];
  sources: TLedgerFilterOption<S>[];
  selectedRoles: Set<R>;
  selectedSources: Set<S>;
  onToggleRole: (role: R, checked: boolean) => void;
  onToggleSource: (source: S, checked: boolean) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation("projectHome");
  const activeCount = selectedRoles.size + selectedSources.size;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant={activeCount > 0 ? "secondary" : "outline"}
            className="h-[30px] gap-1 rounded-full px-3 shadow-none has-[>svg]:px-3"
            data-testid="model-ledger-filter-trigger"
          />
        }
      >
        <FunnelSimpleIcon className="size-4" />
        {t("modelLedger.filter")}
        {activeCount > 0 && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[12px] font-medium leading-[11px] tabular-nums text-primary-foreground">
            {activeCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className="gap-0 rounded-xl border p-0">
        <div className="border-b border-innoveraZinc-100 p-1">
          <div className="flex h-8 items-center justify-between rounded-lg p-2">
            <span className="text-[14px] font-medium leading-[13px] text-innoveraZinc-900">
              {t("modelLedger.filter")}
            </span>
            <button
              type="button"
              disabled={activeCount === 0}
              onClick={onReset}
              data-testid="model-ledger-filter-reset"
              className="text-[14px] font-normal leading-[22px] text-innoveraZinc-700 disabled:cursor-not-allowed disabled:text-innoveraZinc-400"
            >
              {t("modelLedger.reset")}
            </button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto px-2 py-1">
          <FilterSection
            label={t("modelLedger.filterRole")}
            options={roles}
            selected={selectedRoles}
            onToggle={onToggleRole}
            testId="model-ledger-filter-role"
          />
          <FilterSection
            label={t("modelLedger.filterSource")}
            options={sources}
            selected={selectedSources}
            onToggle={onToggleSource}
            testId="model-ledger-filter-source"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
